// src/pages/Appli/RDVAgenda/RDVAgendaPage.jsx
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
  useDeferredValue,
} from "react";
import {
  getAllRDV,
  deleteRDV,
  addRDV,
} from "../../../services/rdvAgendaServices";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Button from "../../../components/Button/Button";
import ButtonEdit from "../../../components/Button/ButtonEdit";
import ButtonDelete from "../../../components/Button/ButtonDelete";

import { FaPlus, FaClone } from "react-icons/fa";

import CreateRDVAgendaModal from "./CreateRDVAgendaModal";
import DuplicateWeekRDVModal from "./DuplicateWeekRDVModal";

/* =========================
   Helpers
   ========================= */
const pad2 = (n) => String(n).padStart(2, "0");
const toISODate = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const parseISODate = (value) => {
  if (!value) return null;
  const s = String(value);

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const d = new Date(`${s}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
};

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const startOfMonday = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfSunday = (date) => {
  const monday = startOfMonday(date);
  const sunday = addDays(monday, 6);
  sunday.setHours(23, 59, 59, 999);
  return sunday;
};

const hhmm = (t) => (t ? String(t).slice(0, 5) : "");

const toHHMMSS = (t) => {
  if (!t) return "";
  const s = String(t);
  return s.length === 5 ? `${s}:00` : s;
};

const minutesOf = (hhmmStr) => {
  if (!hhmmStr) return 0;
  const [h, m] = hhmmStr.split(":").map((x) => parseInt(x, 10));
  return h * 60 + m;
};

const addMinutesToHHMM = (hhmmStr, minsToAdd) => {
  const base = minutesOf(hhmmStr);
  let total = base + minsToAdd;

  // Clamp pour éviter "24:15" invalide dans input type="time"
  if (total < 0) total = 0;
  if (total > 23 * 60 + 59) total = 23 * 60 + 59;

  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${pad2(h)}:${pad2(m)}`;
};

const isReservedRDV = (rdv) =>
  !!(rdv?.nom_client && String(rdv.nom_client).trim().length > 0);

/* =========================
   Component
   ========================= */
const RDVAgendaPage = () => {
  const [rdvs, setRdvs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modales
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedRDV, setSelectedRDV] = useState(null);
  const [presetRDV, setPresetRDV] = useState(null);
  const [isDupWeekOpen, setIsDupWeekOpen] = useState(false);

  // Filtres
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const qDeferred = useDeferredValue(q); // ✅ évite de recalculer filtre/tri à chaque frappe

  const [dateFrom, setDateFrom] = useState(
    toISODate(startOfMonday(new Date())),
  );
  const [dateTo, setDateTo] = useState(toISODate(endOfSunday(new Date())));

  // Quick add
  const [quickDate, setQuickDate] = useState(toISODate(new Date()));
  const [quickStart, setQuickStart] = useState("09:00");
  const [quickDuration, setQuickDuration] = useState(30);
  const [quickLieu, setQuickLieu] = useState("");
  const [quickNote, setQuickNote] = useState("");

  const quickEnd = useMemo(
    () => addMinutesToHHMM(quickStart, Number(quickDuration || 0)),
    [quickStart, quickDuration],
  );

  // Formatter stable (sinon recréé 2000x si grosse liste)
  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat("fr-FR", {
        weekday: "short",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }),
    [],
  );

  // ✅ garde-fou anti-spam fetch
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchRDV = useCallback(async () => {
    if (inFlightRef.current) return; // ✅ évite boucle qui spam l’API
    inFlightRef.current = true;

    setLoading(true);
    try {
      const data = await getAllRDV();
      if (!mountedRef.current) return;
      setRdvs(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur chargement RDV :", error);
      toast.error("Impossible de charger les RDV.");
    } finally {
      if (mountedRef.current) setLoading(false);
      inFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    fetchRDV();
  }, [fetchRDV]);

  // ✅ pré-normalisation : on parse/compute une fois
  const normalized = useMemo(() => {
    return (rdvs || []).map((r) => {
      const d = parseISODate(r.date_rdv);
      const start = minutesOf(hhmm(r.heure_debut));
      const reserved = isReservedRDV(r);
      return {
        ...r,
        _dateObj: d,
        _dateTs: d ? d.getTime() : 0,
        _startMin: start,
        _reserved: reserved,
      };
    });
  }, [rdvs]);

  const filtered = useMemo(() => {
    const from = parseISODate(dateFrom);
    const to = parseISODate(dateTo);
    const query = (qDeferred || "").trim().toLowerCase();

    return normalized
      .filter((r) => {
        // dates
        if (from || to) {
          if (!r._dateObj) return false;
          if (from && r._dateObj < from) return false;
          if (to && r._dateObj > to) return false;
        }

        // statut
        if (statusFilter === "reserved" && !r._reserved) return false;
        if (statusFilter === "available" && r._reserved) return false;

        // recherche
        if (query) {
          const hay = [
            r.nom_client,
            r.prenom_client,
            r.tel_client,
            r.lieu,
            r.note_client,
            r.date_rdv,
            r.heure_debut,
            r.heure_fin,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          if (!hay.includes(query)) return false;
        }

        return true;
      })
      .sort((a, b) => {
        // sort numérique, plus rapide que localeCompare sur strings
        if (a._dateTs !== b._dateTs) return a._dateTs - b._dateTs;
        return a._startMin - b._startMin;
      });
  }, [normalized, dateFrom, dateTo, qDeferred, statusFilter]);

  const counts = useMemo(() => {
    let reserved = 0;
    let available = 0;
    for (const r of filtered) r._reserved ? reserved++ : available++;
    return { total: filtered.length, reserved, available };
  }, [filtered]);

  const openCreate = useCallback(() => {
    setSelectedRDV(null);
    setPresetRDV(null);
    setIsFormOpen(true);
  }, []);

  const openEdit = useCallback((rdv) => {
    setSelectedRDV(rdv);
    setPresetRDV(null);
    setIsFormOpen(true);
  }, []);

  const openDuplicate = useCallback((rdv) => {
    const d = parseISODate(rdv?.date_rdv) || new Date();
    const nextWeek = addDays(d, 7);

    setSelectedRDV(null);
    setPresetRDV({
      ...rdv,
      id: undefined,
      date_rdv: toISODate(nextWeek),
      nom_client: "",
      prenom_client: "",
      tel_client: "",
    });
    setIsFormOpen(true);
  }, []);

  const handleDelete = useCallback(
    async (rdv) => {
      if (!rdv?.id) return;
      if (!window.confirm("Supprimer ce RDV ?")) return;

      try {
        await deleteRDV(rdv.id);
        toast.success("RDV supprimé ✅");
        await fetchRDV();
      } catch (error) {
        console.error("Erreur suppression RDV :", error);
        toast.error("Erreur lors de la suppression.");
      }
    },
    [fetchRDV],
  );

  const handleQuickAddAvailable = useCallback(async () => {
    const payload = {
      nom_client: "",
      prenom_client: "",
      tel_client: "",
      date_rdv: quickDate,
      heure_debut: toHHMMSS(quickStart),
      heure_fin: toHHMMSS(quickEnd),
      lieu: quickLieu,
      note_client: quickNote,
    };

    if (!payload.date_rdv || !payload.heure_debut || !payload.heure_fin) {
      toast.error("Date + heure début/fin obligatoires.");
      return;
    }
    if (minutesOf(quickEnd) <= minutesOf(quickStart)) {
      toast.error("L'heure de fin doit être après l'heure de début.");
      return;
    }

    try {
      await addRDV(payload);
      toast.success("Créneau disponible ajouté ✅");
      setQuickNote("");
      await fetchRDV();
    } catch (error) {
      console.error("Erreur quick-add RDV :", error);
      toast.error("Erreur lors de l'ajout du créneau.");
    }
  }, [quickDate, quickStart, quickEnd, quickLieu, quickNote, fetchRDV]);

  const setThisWeek = useCallback(() => {
    const now = new Date();
    setDateFrom(toISODate(startOfMonday(now)));
    setDateTo(toISODate(endOfSunday(now)));
  }, []);

  const setNextWeek = useCallback(() => {
    const now = new Date();
    const nextMonday = addDays(startOfMonday(now), 7);
    setDateFrom(toISODate(nextMonday));
    setDateTo(toISODate(endOfSunday(nextMonday)));
  }, []);

  const closeForm = useCallback(() => {
    setIsFormOpen(false);
    setSelectedRDV(null);
    setPresetRDV(null);
  }, []);

  const onDupDone = useCallback(async () => {
    setIsDupWeekOpen(false);
    await fetchRDV();
  }, [fetchRDV]);

  const defaultSourceDate = useMemo(
    () => dateFrom || toISODate(new Date()),
    [dateFrom],
  );

  const fmtWeek = (monday) =>
    new Intl.DateTimeFormat("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(monday);

  // Liste des semaines qui contiennent au moins 1 RDV
  const weeksWithRDV = useMemo(() => {
    const map = new Map(); // key = YYYY-MM-DD du lundi
    for (const r of rdvs) {
      const d = parseISODate(r.date_rdv);
      if (!d) continue;
      const monday = startOfMonday(d);
      const key = toISODate(monday);
      if (!map.has(key)) map.set(key, monday);
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, monday]) => ({
        key,
        monday,
        label: `Semaine du ${fmtWeek(monday)}`,
      }));
  }, [rdvs]);

  const showAllRDV = useCallback(() => {
    // input[type=date] accepte "" => pas de filtre
    setDateFrom("");
    setDateTo("");
  }, []);

  const goToWeek = useCallback((mondayISO) => {
    const monday = parseISODate(mondayISO) || startOfMonday(new Date());
    setDateFrom(toISODate(startOfMonday(monday)));
    setDateTo(toISODate(endOfSunday(monday)));
  }, []);

  const shiftWeekFromFilter = useCallback(
    (deltaWeeks) => {
      const base = parseISODate(dateFrom) || startOfMonday(new Date());
      const monday = addDays(startOfMonday(base), deltaWeeks * 7);
      setDateFrom(toISODate(monday));
      setDateTo(toISODate(endOfSunday(monday)));
    },
    [dateFrom],
  );

  return (
    <div className="p-6 bg-white dark:bg-gray-800 rounded-md shadow-md">
      <div className="max-w-[1280px] mx-auto">
        <h2 className="text-2xl font-extrabold mb-2 dark:text-gray-100 text-center">
          Gestion des Rendez-vous
        </h2>

        <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between mb-6">
          <div className="flex gap-2 flex-wrap justify-center md:justify-start">
            <Button
              text="Ajouter un RDV"
              icon={FaPlus}
              className="btn-primary gap-2"
              onClick={openCreate}
            />
            <Button
              text="Dupliquer une semaine"
              icon={FaClone}
              className="btn-secondary gap-2"
              onClick={() => setIsDupWeekOpen(true)}
            />
          </div>

          <div className="flex gap-2 flex-wrap justify-center md:justify-end items-center">
            <button
              type="button"
              onClick={() => shiftWeekFromFilter(-1)}
              className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Semaine -1
            </button>

            <button
              type="button"
              onClick={setThisWeek}
              className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cette semaine
            </button>

            <button
              type="button"
              onClick={setNextWeek}
              className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Semaine prochaine
            </button>

            <button
              type="button"
              onClick={() => shiftWeekFromFilter(+1)}
              className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Semaine +1
            </button>

            <button
              type="button"
              onClick={showAllRDV}
              className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-bold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
              title="Afficher tous les RDV (toutes dates)"
            >
              Tous les RDV
            </button>

            <select
              value=""
              onChange={(e) => {
                const v = e.target.value;
                if (v) goToWeek(v);
              }}
              className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200"
              title="Aller à une semaine qui contient des RDV"
            >
              <option value="">📅 Aller à une semaine…</option>
              {weeksWithRDV.map((w) => (
                <option key={w.key} value={w.key}>
                  {w.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Quick add */}
        <div className="mb-8 p-4 rounded-2xl border bg-gray-50 dark:bg-gray-900/30">
          <div className="mt-4 grid grid-cols-1 md:grid-cols-6 gap-3">
            <input
              type="date"
              value={quickDate}
              onChange={(e) => setQuickDate(e.target.value)}
              className="rounded-lg border px-3 py-2 text-sm"
            />
            <input
              type="time"
              value={quickStart}
              onChange={(e) => setQuickStart(e.target.value)}
              className="rounded-lg border px-3 py-2 text-sm"
            />
            <select
              value={quickDuration}
              onChange={(e) => setQuickDuration(Number(e.target.value))}
              className="rounded-lg border px-3 py-2 text-sm"
            >
              <option value={15}>15 min</option>
              <option value={30}>30 min</option>
              <option value={45}>45 min</option>
              <option value={60}>60 min</option>
              <option value={90}>90 min</option>
              <option value={120}>120 min</option>
            </select>
            <input
              type="time"
              value={quickEnd}
              disabled
              className="rounded-lg border px-3 py-2 text-sm"
            />
            <input
              type="text"
              value={quickLieu}
              onChange={(e) => setQuickLieu(e.target.value)}
              placeholder="Lieu"
              className="rounded-lg border px-3 py-2 text-sm md:col-span-2"
            />
            <input
              type="text"
              value={quickNote}
              onChange={(e) => setQuickNote(e.target.value)}
              placeholder="Note"
              className="rounded-lg border px-3 py-2 text-sm md:col-span-2"
            />
          </div>

          <div className="mt-4 flex justify-end">
            <Button
              text="Ajouter le créneau"
              icon={FaPlus}
              onClick={handleQuickAddAvailable}
              className="btn-primary gap-2"
            />
          </div>
        </div>

        {/* Stats */}
        <div className="mb-4 flex flex-wrap gap-2">
          <span className="px-3 py-1 rounded-full text-xs font-bold bg-gray-100 dark:bg-gray-700">
            Total: {counts.total}
          </span>
          <span className="px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800">
            Disponibles: {counts.available}
          </span>
          <span className="px-3 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-800">
            Réservés: {counts.reserved}
          </span>
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-2xl border border-gray-200 dark:border-gray-700">
          <table className="min-w-full bg-white dark:bg-gray-800">
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-10 text-center text-gray-600 dark:text-gray-300"
                  >
                    Chargement...
                  </td>
                </tr>
              )}

              {!loading && !filtered.length && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-10 text-center text-gray-600 dark:text-gray-300"
                  >
                    Aucun RDV trouvé avec ces filtres.
                  </td>
                </tr>
              )}

              {!loading &&
                filtered.map((rdv) => {
                  const dateLabel = rdv._dateObj
                    ? dateFmt.format(rdv._dateObj)
                    : "—";
                  const reserved = rdv._reserved;

                  return (
                    <tr
                      key={rdv.id}
                      className={
                        reserved
                          ? "bg-green-50 dark:bg-green-900/20"
                          : "bg-white dark:bg-gray-800"
                      }
                    >
                      {" "}
                      <td className="px-4 py-3 text-sm">{dateLabel}</td>{" "}
                      <td className="px-4 py-3 text-sm">
                        {" "}
                        {hhmm(rdv.heure_debut)} → {hhmm(rdv.heure_fin)}{" "}
                      </td>{" "}
                      <td className="px-4 py-3 text-sm">
                        {" "}
                        <span
                          className={
                            "inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ring-1 " +
                            (reserved
                              ? "bg-green-200 text-green-900 ring-green-300 dark:bg-green-900/40 dark:text-green-100 dark:ring-green-700"
                              : "bg-gray-100 text-gray-800 ring-gray-200 dark:bg-gray-700 dark:text-gray-100 dark:ring-gray-600")
                          }
                        >
                          {" "}
                          {reserved ? "✅ Réservé" : "⏳ Disponible"}{" "}
                        </span>{" "}
                      </td>{" "}
                      <td className="px-4 py-3 text-sm">
                        {" "}
                        {reserved
                          ? `${rdv.nom_client || ""} ${rdv.prenom_client || ""}`.trim()
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {rdv.lieu || rdv.note_client || "—"}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex justify-center gap-2 flex-wrap">
                          <ButtonEdit onClick={() => openEdit(rdv)} />
                          <button
                            type="button"
                            onClick={() => openDuplicate(rdv)}
                            className="w-8 h-8 rounded-lg bg-blue-500 text-white flex items-center justify-center"
                            title="Dupliquer ce RDV"
                          >
                            <FaClone size={14} />
                          </button>
                          <ButtonDelete onClick={() => handleDelete(rdv)} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modales */}
      <CreateRDVAgendaModal
        isOpen={isFormOpen}
        onClose={closeForm}
        refreshList={fetchRDV}
        rdvToEdit={selectedRDV}
        preset={presetRDV}
        onDelete={selectedRDV ? () => handleDelete(selectedRDV) : null}
      />

      <DuplicateWeekRDVModal
        isOpen={isDupWeekOpen}
        onClose={() => setIsDupWeekOpen(false)}
        rdvs={rdvs}
        onDone={onDupDone}
        defaultSourceDate={defaultSourceDate}
      />

      <ToastContainer />
    </div>
  );
};

export default RDVAgendaPage;
